<script lang="ts">
    import { onMount } from "svelte";
    import appStore from "@/utils/store";
    import { goTo } from "@/utils/router";
    import { translate } from "@/utils/format";
    import { isIOS } from "@/utils/platform";

    import {
        updateReferralCodeSE,
        createStripeAccountSE,
        createStripeAccountLinkSE,
        checkStripeAccountSE,
    } from "@/services/referrals";

    import { uploadFotoSE } from "@/utils/user";
    import { updateDocById } from "@/services/firebase";
    import { Dialog, File as FilePicker, Spinner } from "@/components";

    type StepId = 1 | 2 | 3 | 4 | 5;

    let loading = $state(true);
    let saving = $state(false);
    let user = $state<any>(null);

    let step = $state<StepId>(1);

    let userName = $state("");
    let userImage = $state<string | null>(null);

    let referralCode = $state("");
    let referralError = $state<string | null>(null);

    let stripeAccountId = $state<string | null>(null);
    let stripeReady = $state(false);

    let showErrorDialog = $state(false);
    let errorDialogText = $state("");

    const referralCodeTrimmed = $derived((referralCode || "").trim());
    const userNameTrimmed = $derived((userName || "").trim());

    const minStepsDone = $derived(
        !!userNameTrimmed &&
            !!referralCodeTrimmed &&
            referralCodeTrimmed !== String(user?.id || "").trim(),
    );

    const canGoNext = $derived(
        !saving &&
            (step === 1 ||
                (step === 2 && !!userNameTrimmed) ||
                (step === 3 && isValidReferralCode(referralCodeTrimmed)) ||
                step === 4 ||
                step === 5),
    );

    function isValidReferralCode(v: string): boolean {
        if (!v) return false;
        if (v.length < 3 || v.length > 30) return false;
        return /^[a-z0-9][a-z0-9-_]*[a-z0-9]$/i.test(v);
    }

    function needsPartnerOnboarding(u: any): boolean {
        if (!u) return true;
        const id = String(u?.id || "").trim();
        const currentCode = String(u?.referralCode || "").trim();
        const currentName = String(u?.name || "").trim();

        if (!currentName) return true;
        if (!currentCode) return true;
        if (id && currentCode === id) return true;
        return false;
    }

    async function persistUser(patch: Record<string, any>) {
        const merged = { ...(user || {}), ...(patch || {}) };
        user = merged;
        appStore.set("user", merged);

        if (merged?.firebaseUser) {
            await updateDocById("users", merged.firebaseUser, patch);
        }
    }

    async function refreshStripeStatus() {
        if (!user?.id || !stripeAccountId) return;
        try {
            const res = await checkStripeAccountSE(user.id, stripeAccountId);
            stripeReady = !!res?.ready;
        } catch {
            // ignore
        }
    }

    async function buildUploadPayload(file: globalThis.File) {
        const imageBitmap = await createImageBitmap(file);

        const canvas = document.createElement("canvas");
        const maxHeight = isIOS() ? 780 : 800;
        const scale = maxHeight / imageBitmap.height;
        canvas.width = Math.max(1, Math.round(imageBitmap.width * scale));
        canvas.height = Math.max(1, Math.round(maxHeight));

        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        ctx.drawImage(imageBitmap, 0, 0, canvas.width, canvas.height);

        const blob: Blob | null = await new Promise((resolve) =>
            canvas.toBlob(resolve, "image/jpeg", 0.9),
        );
        if (!blob) return null;

        const arrayBuffer = await blob.arrayBuffer();
        const buffer = Array.from(new Uint8Array(arrayBuffer));

        return {
            buffer,
            type: "image/jpeg",
            name: file.name || "logo.jpg",
        };
    }

    async function handleLogoChange(
        fileOrCommand: globalThis.File | Blob | "remove",
    ) {
        if (fileOrCommand === "remove") {
            userImage = null;
            await persistUser({ image: "" });
            return;
        }

        try {
            saving = true;

            const file =
                fileOrCommand instanceof Blob &&
                !(fileOrCommand instanceof File)
                    ? new globalThis.File([fileOrCommand], "logo.jpg", {
                          type: fileOrCommand.type || "image/jpeg",
                      })
                    : (fileOrCommand as File);

            const payload = await buildUploadPayload(file);
            if (!payload) throw new Error("upload_payload_error");

            const res = await uploadFotoSE(
                user?.firebaseUser,
                JSON.stringify({ file: payload }),
            );

            const url =
                typeof res === "string"
                    ? res
                    : typeof (res as any)?.url === "string"
                      ? (res as any).url
                      : typeof (res as any)?.data === "string"
                        ? (res as any).data
                        : null;

            if (!url) throw new Error("upload_failed");

            userImage = url;
            await persistUser({ image: url });
        } catch (e) {
            console.error("Logo upload error:", e);
            errorDialogText = translate(
                "PARTNER_ONB_LOGO_ERROR",
                "No se ha podido subir el logo. Inténtalo de nuevo.",
            );
            showErrorDialog = true;
        } finally {
            saving = false;
        }
    }

    async function saveStep2() {
        await persistUser({
            name: userNameTrimmed,
            image: userImage,
        });
    }

    async function saveStep3() {
        referralError = null;
        if (!isValidReferralCode(referralCodeTrimmed)) {
            referralError = translate(
                "PARTNER_ONB_CODE_INVALID",
                "El código debe tener entre 3 y 30 caracteres y usar letras, números, guiones o guion bajo.",
            );
            return;
        }

        if (
            String(user?.id || "").trim() &&
            referralCodeTrimmed === String(user.id).trim()
        ) {
            referralError = translate(
                "PARTNER_ONB_CODE_NOT_DEFAULT",
                "Elige un código distinto a tu ID.",
            );
            return;
        }

        await updateReferralCodeSE(user.id, referralCodeTrimmed);
        await persistUser({ referralCode: referralCodeTrimmed });

        // Marcar onboarding como completado (mínimo pasos 1-3)
        await persistUser({
            partnerOnboarding: {
                v: 1,
                completedAt: Date.now(),
            },
        });
    }

    async function ensureStripeAccount() {
        if (stripeAccountId) return;
        const res = await createStripeAccountSE(user.id, referralCodeTrimmed);
        stripeAccountId = res?.accountId || null;
        await persistUser({ stripeAccountId });
        await refreshStripeStatus();
    }

    function finish() {
        goTo("/dashboard");
    }

    async function next() {
        if (saving) return;

        try {
            saving = true;

            if (step === 1) {
                step = 2;
                return;
            }

            if (step === 2) {
                await saveStep2();
                step = 3;
                return;
            }

            if (step === 3) {
                await saveStep3();
                if (minStepsDone) {
                    // Stripe opcional
                    step = 4;
                }
                return;
            }

            if (step === 4) {
                // Stripe opcional: no obligamos
                step = 5;
                return;
            }

            if (step === 5) {
                finish();
                return;
            }
        } catch (err: any) {
            console.error("Onboarding next error:", err);
            if (step === 3 && err?.response?.status === 409) {
                referralError = translate(
                    "PARTNER_ONB_CODE_TAKEN",
                    "Ese código ya está en uso. Prueba otro.",
                );
                return;
            }

            errorDialogText = translate(
                "PARTNER_ONB_GENERIC_ERROR",
                "Ha ocurrido un error. Inténtalo de nuevo.",
            );
            showErrorDialog = true;
        } finally {
            saving = false;
        }
    }

    function prev() {
        if (saving) return;
        if (step === 1) return;
        if (step === 2) step = 1;
        else if (step === 3) step = 2;
        else if (step === 4) step = 3;
        else if (step === 5) step = 4;
    }

    onMount(async () => {
        loading = true;
        try {
            user = appStore.get("user") || null;
            if (!user) {
                goTo("/logout");
                return;
            }

            // Si ya está todo listo, no mostramos onboarding
            if (!needsPartnerOnboarding(user)) {
                goTo("/dashboard");
                return;
            }

            userName = String(user?.name || "").trim();
            userImage = (user?.image || null) as string | null;

            referralCode = String(user?.referralCode || "").trim();

            stripeAccountId = (user?.stripeAccountId || null) as string | null;
            await refreshStripeStatus();
        } finally {
            loading = false;
        }
    });
</script>

<div class="partnerOnb">
    {#if loading}
        <div class="partnerOnb__loading">
            <Spinner text="" />
        </div>
    {:else}
        <div class="partnerOnb__card">
            <div class="partnerOnb__header">
                <div class="partnerOnb__step">
                    {translate("PARTNER_ONB_STEP", "Paso")}
                    {step}/5
                </div>
                <h1 class="partnerOnb__title">
                    {translate("PARTNER_ONB_TITLE", "Alta de partner en Caloo")}
                </h1>
            </div>

            {#if step === 1}
                <div class="partnerOnb__content">
                    <h2>
                        {translate(
                            "PARTNER_ONB_WELCOME_TITLE",
                            "Bienvenido/a al Panel de Partner",
                        )}
                    </h2>
                    <p>
                        {translate(
                            "PARTNER_ONB_WELCOME_DESC",
                            "Vamos a dejar tu perfil listo para compartir enlace, medir resultados y cobrar comisiones. Stripe es opcional y podrás configurarlo más tarde.",
                        )}
                    </p>
                    <div class="partnerOnb__bullets">
                        <div class="partnerOnb__bullet">
                            <span class="partnerOnb__dot"></span>
                            <span>
                                {translate(
                                    "PARTNER_ONB_WELCOME_B1",
                                    "Tu nombre y logo",
                                )}
                            </span>
                        </div>
                        <div class="partnerOnb__bullet">
                            <span class="partnerOnb__dot"></span>
                            <span>
                                {translate(
                                    "PARTNER_ONB_WELCOME_B2",
                                    "Tu código de partner",
                                )}
                            </span>
                        </div>
                        <div class="partnerOnb__bullet">
                            <span class="partnerOnb__dot"></span>
                            <span>
                                {translate(
                                    "PARTNER_ONB_WELCOME_B3",
                                    "Stripe (opcional)",
                                )}
                            </span>
                        </div>
                    </div>

                    <p class="partnerOnb__trust">
                        {translate(
                            "PARTNER_ONB_WELCOME_TRUST",
                            "Nada de esto es definitivo: podrás cambiar tu nombre, imagen y código más adelante desde tu perfil o el dashboard.",
                        )}
                    </p>
                </div>
            {/if}

            {#if step === 2}
                <div class="partnerOnb__content">
                    <h2>
                        {translate("PARTNER_ONB_BRAND_TITLE", "Tu perfil")}
                    </h2>
                    <p>
                        {translate(
                            "PARTNER_ONB_BRAND_DESC",
                            "Usaremos tu nombre y tu imagen para que todo se vea consistente.",
                        )}
                    </p>

                    <div class="partnerOnb__brandGrid">
                        <div class="partnerOnb__logo">
                            <FilePicker
                                type={translate(
                                    "PARTNER_ONB_LOGO_BTN",
                                    "Subir imagen",
                                )}
                                onUpdateImage={handleLogoChange}
                                imageProfile={userImage}
                                isUploading={saving}
                            />
                        </div>

                        <div class="partnerOnb__fields">
                            <label class="partnerOnb__label" for="partnerName">
                                {translate(
                                    "PARTNER_ONB_PARTNER_NAME",
                                    "Nombre",
                                )}
                            </label>
                            <input
                                id="partnerName"
                                class="partnerOnb__input"
                                type="text"
                                value={userName}
                                oninput={(e: any) =>
                                    (userName = e.target.value)}
                                placeholder={translate(
                                    "PARTNER_ONB_PARTNER_NAME_PH",
                                    "Ej: Clínica X, Entrenador/a Y...",
                                )}
                            />
                        </div>
                    </div>

                    <p class="partnerOnb__trust">
                        {translate(
                            "PARTNER_ONB_BRAND_TRUST",
                            "Puedes cambiarlo cuando quieras más adelante en tu perfil.",
                        )}
                    </p>
                </div>
            {/if}

            {#if step === 3}
                <div class="partnerOnb__content">
                    <h2>
                        {translate(
                            "PARTNER_ONB_CODE_TITLE",
                            "Tu código de partner",
                        )}
                    </h2>
                    <p>
                        {translate(
                            "PARTNER_ONB_CODE_DESC",
                            "Este código irá en tu enlace y en tus métricas.",
                        )}
                    </p>

                    <label class="partnerOnb__label" for="referralCode">
                        {translate("PARTNER_ONB_CODE_LABEL", "Código")}
                    </label>
                    <input
                        id="referralCode"
                        class="partnerOnb__input"
                        type="text"
                        value={referralCode}
                        oninput={(e: any) => {
                            referralError = null;
                            referralCode = e.target.value;
                        }}
                        placeholder={translate(
                            "PARTNER_ONB_CODE_PH",
                            "Ej: carlos-fit",
                        )}
                    />

                    {#if referralError}
                        <p class="partnerOnb__error">{referralError}</p>
                    {/if}

                    <p class="partnerOnb__hint">
                        {translate(
                            "PARTNER_ONB_CODE_HINT",
                            "Usa 3-30 caracteres. Letras/números/guión/guión bajo.",
                        )}
                    </p>

                    <p class="partnerOnb__trust">
                        {translate(
                            "PARTNER_ONB_CODE_TRUST",
                            "Elige un código fácil de recordar. Si más adelante lo cambias, tu enlace y métricas se actualizarán en el dashboard.",
                        )}
                    </p>
                </div>
            {/if}

            {#if step === 4}
                <div class="partnerOnb__content">
                    <h2>
                        {translate(
                            "PARTNER_ONB_STRIPE_CREATE_TITLE",
                            "Crea tu cuenta de Stripe",
                        )}
                    </h2>
                    <p>
                        {translate(
                            "PARTNER_ONB_STRIPE_CREATE_DESC",
                            "Stripe te permite cobrar comisiones. Puedes hacerlo más tarde.",
                        )}
                    </p>

                    <p class="partnerOnb__trust">
                        {translate(
                            "PARTNER_ONB_STRIPE_TRUST",
                            "Stripe gestiona el proceso de verificación y pagos. Puedes saltarte este paso y completarlo cuando lo necesites.",
                        )}
                    </p>

                    {#if stripeAccountId}
                        <div class="partnerOnb__ok">
                            {translate(
                                "PARTNER_ONB_STRIPE_EXISTS",
                                "Cuenta de Stripe creada.",
                            )}
                        </div>
                    {:else}
                        <button
                            class="partnerOnb__primary"
                            disabled={saving || !minStepsDone}
                            onclick={async () => {
                                try {
                                    saving = true;
                                    await ensureStripeAccount();
                                } catch (e) {
                                    console.error(e);
                                    errorDialogText = translate(
                                        "PARTNER_ONB_STRIPE_CREATE_ERR",
                                        "No se ha podido crear la cuenta de Stripe.",
                                    );
                                    showErrorDialog = true;
                                } finally {
                                    saving = false;
                                }
                            }}
                        >
                            {translate(
                                "PARTNER_ONB_STRIPE_CREATE_BTN",
                                "Crear Stripe ahora",
                            )}
                        </button>
                    {/if}

                    <button
                        class="partnerOnb__secondary"
                        type="button"
                        onclick={finish}
                        disabled={saving}
                    >
                        {translate("PARTNER_ONB_DO_LATER", "Hacerlo más tarde")}
                    </button>
                </div>
            {/if}

            {#if step === 5}
                <div class="partnerOnb__content">
                    <h2>
                        {translate(
                            "PARTNER_ONB_STRIPE_ONBOARD_TITLE",
                            "Completa tu configuración",
                        )}
                    </h2>
                    <p>
                        {translate(
                            "PARTNER_ONB_STRIPE_ONBOARD_DESC",
                            "Puedes terminar el onboarding de Stripe ahora o desde el dashboard.",
                        )}
                    </p>

                    <p class="partnerOnb__trust">
                        {translate(
                            "PARTNER_ONB_STRIPE_ONBOARD_TRUST",
                            "Abriremos Stripe en una nueva pestaña. Cuando termines, vuelve aquí y continúa.",
                        )}
                    </p>

                    {#if stripeAccountId}
                        <div class="partnerOnb__row">
                            <div class="partnerOnb__status">
                                {stripeReady
                                    ? translate(
                                          "PARTNER_ONB_STRIPE_READY",
                                          "Stripe listo",
                                      )
                                    : translate(
                                          "PARTNER_ONB_STRIPE_PENDING",
                                          "Stripe pendiente",
                                      )}
                            </div>
                            <button
                                class="partnerOnb__primary"
                                type="button"
                                disabled={saving}
                                onclick={async () => {
                                    try {
                                        saving = true;
                                        const res =
                                            await createStripeAccountLinkSE(
                                                user.id,
                                                stripeAccountId!,
                                            );
                                        window.open(res.url, "_blank");
                                    } catch (e) {
                                        console.error(e);
                                        errorDialogText = translate(
                                            "PARTNER_ONB_STRIPE_LINK_ERR",
                                            "No se ha podido abrir el onboarding de Stripe.",
                                        );
                                        showErrorDialog = true;
                                    } finally {
                                        saving = false;
                                    }
                                }}
                            >
                                {translate(
                                    "PARTNER_ONB_STRIPE_ONBOARD_BTN",
                                    "Abrir onboarding",
                                )}
                            </button>
                        </div>
                    {/if}

                    <button
                        class="partnerOnb__primary"
                        type="button"
                        onclick={finish}
                        disabled={saving}
                    >
                        {translate("PARTNER_ONB_FINISH", "Ir al dashboard")}
                    </button>
                </div>
            {/if}

            <div class="partnerOnb__footer">
                <button
                    class="partnerOnb__nav"
                    type="button"
                    onclick={prev}
                    disabled={saving || step === 1}
                >
                    {translate("PARTNER_ONB_BACK", "Atrás")}
                </button>

                <button
                    class="partnerOnb__nav partnerOnb__nav--primary"
                    type="button"
                    onclick={next}
                    disabled={!canGoNext}
                >
                    {step === 5
                        ? translate("PARTNER_ONB_DONE", "Terminar")
                        : translate("PARTNER_ONB_NEXT", "Siguiente")}
                </button>
            </div>
        </div>
    {/if}

    <Dialog
        isOpen={showErrorDialog}
        message={errorDialogText}
        labelOk={translate("OK", "Ok")}
        showCancelButton={false}
        onOk={() => (showErrorDialog = false)}
        onCancel={() => (showErrorDialog = false)}
    />
</div>

<style lang="scss">
    @use "./styles/index.scss";
</style>
